// Arrastrar a un agente a otra mesa de su sala (Pau, 09-10). Con ratón, siempre (pulsar sin mover abre el chat); con el
// dedo, solo en «Ordenar» (fuera de ahí el dedo desplaza la oficina). 04-11 (mesas de equipo): el destino es un SITIO
// (geometria.ts `claveSitio`): una mesa de un grupo, o la placa de un grupo entera (= entrar en ese grupo). Máquina de
// estados explícita:
//
//   reposo ──pulsar──▶ pulsado ──mover > 6 px──▶ arrastrando ──soltar──▶ soltado   (pide la mesa de destino)
//                         │                          └──Escape / pointercancel──▶ cancelado
//                         └──soltar / Escape──▶ reposo (fue un clic: lo atiende el botón)
//
// `soltado` y `cancelado` son finales de un instante: vuelven a `reposo` y dejan `acabaDeSoltar()` a true durante esa
// vuelta del bucle, para que el clic que el navegador manda al soltar no abra el chat.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const UMBRAL_ARRASTRE_PX = 6;

export interface Agarre {
  agentId: string;
  projectId: string;
  /** Sitio del que sale (geometria.ts `claveSitio`). */
  desde: string;
  pointerId: number;
  x0: number;
  y0: number;
}
/** Lo que hay bajo el puntero: el sitio de destino (null = ninguno) y su posición en la capa, para el fantasma. */
export interface Bajo {
  sobre: string | null;
  px: number;
  py: number;
}
export type Arrastrando = { fase: 'arrastrando' } & Agarre & Bajo;
export type EstadoArrastre = { fase: 'reposo' } | ({ fase: 'pulsado' } & Agarre) | Arrastrando;

export type EventoArrastre =
  | { tipo: 'pulsar'; agarre: Agarre }
  /** `medir` solo se llama si el puntero ya arrastra (pasado el umbral): es donde van los efectos (desplazar, buscar mesa). */
  | { tipo: 'mover'; pointerId: number; x: number; y: number; medir: () => Bajo }
  | { tipo: 'soltar'; pointerId: number; cancelado: boolean }
  | { tipo: 'escape' };

export interface Paso {
  estado: EstadoArrastre;
  /** Cómo acabó un arrastre de verdad (null si no acabó, o si solo fue un clic). */
  fin: 'soltado' | 'cancelado' | null;
  /** Al soltar en otro sitio de verdad: a cuál. */
  mover: { projectId: string; agentId: string; sitio: string } | null;
}

export const REPOSO: EstadoArrastre = { fase: 'reposo' };
const igual = (estado: EstadoArrastre): Paso => ({ estado, fin: null, mover: null });

/** La máquina, PURA (salvo lo que haga `medir`). */
export function avanzar(e: EstadoArrastre, ev: EventoArrastre): Paso {
  if (ev.tipo === 'pulsar') return igual(e.fase === 'reposo' ? { fase: 'pulsado', ...ev.agarre } : e);
  if (e.fase === 'reposo') return igual(e);
  if (ev.tipo === 'escape') return { estado: REPOSO, fin: e.fase === 'arrastrando' ? 'cancelado' : null, mover: null };
  if (ev.pointerId !== e.pointerId) return igual(e);
  if (ev.tipo === 'mover') {
    if (e.fase === 'pulsado' && Math.hypot(ev.x - e.x0, ev.y - e.y0) < UMBRAL_ARRASTRE_PX) return igual(e);
    const { agentId, projectId, desde, pointerId, x0, y0 } = e;
    return igual({ fase: 'arrastrando', agentId, projectId, desde, pointerId, x0, y0, ...ev.medir() });
  }
  // soltar
  if (e.fase === 'pulsado') return igual(REPOSO);
  if (ev.cancelado) return { estado: REPOSO, fin: 'cancelado', mover: null };
  const mover = e.sobre !== null && e.sobre !== e.desde ? { projectId: e.projectId, agentId: e.agentId, sitio: e.sobre } : null;
  return { estado: REPOSO, fin: 'soltado', mover };
}

export interface OpcionesArrastre {
  /** Lo que hay bajo el puntero (en coordenadas de pantalla) para un agente de esa sala. */
  mesaBajo: (clientX: number, clientY: number, projectId: string) => Bajo;
  /** Al pasar del umbral (el arrastre empieza de verdad): p. ej. cerrar la tarjeta. */
  alEmpezar: () => void;
  /** En cada movimiento ya arrastrando, antes de medir: p. ej. desplazar la vista cerca del borde. */
  alMover?: (clientX: number, clientY: number) => void;
  alSoltarEnMesa: (projectId: string, agentId: string, sitio: string) => void;
  /** Soltó fuera de cualquier sitio de su sala (en la capa: px, py), p. ej. sobre otra sala. */
  alSoltarFuera?: (projectId: string, px: number, py: number) => void;
}

interface Puntero {
  button: number;
  pointerId: number;
  clientX: number;
  clientY: number;
}

export function useArrastreMesas(opciones: OpcionesArrastre) {
  const op = useRef(opciones);
  op.current = opciones;
  const estado = useRef<EstadoArrastre>(REPOSO);
  /** Lo que se pinta (fantasma, mesa de destino): solo mientras arrastra, un repintado por fotograma. */
  const [arrastre, setArrastre] = useState<Arrastrando | null>(null);
  const tras = useRef(false);
  const quitar = useRef<() => void>(() => undefined);
  useEffect(() => () => quitar.current(), []);

  const empezar = useCallback((e: Puntero, quien: Pick<Agarre, 'agentId' | 'projectId' | 'desde'>, permitido: boolean) => {
    if (!permitido || e.button !== 0 || estado.current.fase !== 'reposo') return;
    estado.current = avanzar(estado.current, { tipo: 'pulsar', agarre: { ...quien, pointerId: e.pointerId, x0: e.clientX, y0: e.clientY } }).estado;
    let fotograma = 0;
    const aplicar = (ev: EventoArrastre) => {
      const antes = estado.current;
      const paso = avanzar(antes, ev);
      estado.current = paso.estado;
      if (paso.estado.fase === 'reposo') {
        quitar.current();
        cancelAnimationFrame(fotograma);
        if (paso.fin) {
          tras.current = true;
          setTimeout(() => (tras.current = false), 0);
        }
        setArrastre(null);
        if (paso.mover) op.current.alSoltarEnMesa(paso.mover.projectId, paso.mover.agentId, paso.mover.sitio);
        else if (paso.fin === 'soltado' && antes.fase === 'arrastrando' && antes.sobre === null) op.current.alSoltarFuera?.(antes.projectId, antes.px, antes.py);
      } else if (paso.estado !== antes && paso.estado.fase === 'arrastrando') {
        cancelAnimationFrame(fotograma);
        fotograma = requestAnimationFrame(() => setArrastre(estado.current.fase === 'arrastrando' ? estado.current : null));
      }
    };
    const mover = (ev: PointerEvent) =>
      aplicar({
        tipo: 'mover',
        pointerId: ev.pointerId,
        x: ev.clientX,
        y: ev.clientY,
        medir: () => {
          if (estado.current.fase === 'pulsado') op.current.alEmpezar();
          op.current.alMover?.(ev.clientX, ev.clientY);
          return op.current.mesaBajo(ev.clientX, ev.clientY, quien.projectId);
        },
      });
    const soltar = (ev: PointerEvent) => aplicar({ tipo: 'soltar', pointerId: ev.pointerId, cancelado: ev.type !== 'pointerup' });
    const tecla = (ev: KeyboardEvent) => ev.key === 'Escape' && aplicar({ tipo: 'escape' });
    quitar.current = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
      window.removeEventListener('keydown', tecla);
      quitar.current = () => undefined;
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
    window.addEventListener('keydown', tecla);
  }, []);

  const acciones = useMemo(
    () => ({
      empezar,
      /** Hay un dedo o un botón agarrando a alguien (pulsado o arrastrando). */
      ocupado: () => estado.current.fase !== 'reposo',
      /** El clic que llega justo tras soltar (o cancelar) un arrastre no es un clic de verdad. */
      acabaDeSoltar: () => tras.current,
    }),
    [empezar],
  );
  return { arrastre, ...acciones };
}
